import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Enterprise Pagination Component
 * Full-featured pagination with page navigation, size selection, and info display
 */
export default function Pagination({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  pageSize = 25,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  showInfo = true,
  showPageSize = true,
  isRTL: _isRTLProp,
  disabled = false,
  t: _tProp
}) {
  const { t: tI18n, i18n } = useTranslation();
  const t = _tProp || tI18n;
  const isRTL = _isRTLProp !== undefined ? _isRTLProp : (i18n.language === 'ar' || i18n.language === 'he');

  // Calculate display range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of visible range
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust range to always show 3 middle pages
      if (currentPage <= 3) {
        end = 4;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      // Add ellipsis before middle pages if needed
      if (start > 2) {
        pages.push('...');
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis after middle pages if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '36px',
    height: '36px',
    padding: '0 0.5rem',
    border: '1px solid var(--color-border, #e5e7eb)',
    backgroundColor: 'var(--color-surface, #ffffff)',
    color: 'var(--color-text-secondary, #6b7280)',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'all 0.15s ease'
  };

  const activeButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'var(--color-primary, #2563eb)',
    borderColor: 'var(--color-primary, #2563eb)',
    color: 'var(--color-on-primary, #fff)'
  };

  const disabledButtonStyle = {
    ...buttonStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem',
        padding: '1rem 0',
        direction: isRTL ? 'rtl' : 'ltr'
      }}
    >
      {/* Page Size Selector & Info */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        {/* Page Size Selector */}
        {showPageSize && onPageSizeChange && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary, #6b7280)'
            }}>
              {t('pagination.show')}
            </span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              disabled={disabled}
              style={{
                padding: isRTL ? '0.5rem 0.75rem 0.5rem 2rem' : '0.5rem 2rem 0.5rem 0.75rem',
                border: '1px solid var(--color-border, #e5e7eb)',
                borderRadius: '0.375rem',
                backgroundColor: 'var(--color-surface, #ffffff)',
                fontSize: '0.875rem',
                color: 'var(--color-text-primary, #1f2937)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: isRTL ? 'left 0.5rem center' : 'right 0.5rem center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '1.25rem'
              }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
            <span style={{
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary, #6b7280)'
            }}>
              {t('pagination.entries')}
            </span>
          </div>
        )}

        {/* Info Display */}
        {showInfo && (
          <div style={{
            fontSize: '0.875rem',
            color: 'var(--color-text-secondary, #6b7280)'
          }}>
            {t('pagination.showing')}{' '}
            <span style={{ fontWeight: '600', color: 'var(--color-text-primary, #1f2937)' }}>{startItem}</span>
            {' '}-{' '}
            <span style={{ fontWeight: '600', color: 'var(--color-text-primary, #1f2937)' }}>{endItem}</span>
            {' '}{t('pagination.of')}{' '}
            <span style={{ fontWeight: '600', color: 'var(--color-text-primary, #1f2937)' }}>{totalItems}</span>
            {' '}{t('pagination.results')}
          </div>
        )}
      </div>

      {/* Page Navigation */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem'
        }}>
          {/* First Page */}
          <button
            onClick={() => onPageChange(1)}
            disabled={disabled || currentPage === 1}
            style={disabled || currentPage === 1 ? disabledButtonStyle : buttonStyle}
            title={t('pagination.firstPage')}
            aria-label={t('pagination.goToFirstPage')}
          >
            {isRTL ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>

          {/* Previous Page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={disabled || currentPage === 1}
            style={disabled || currentPage === 1 ? disabledButtonStyle : buttonStyle}
            title={t('common.previousPage')}
            aria-label={t('pagination.goToPreviousPage')}
          >
            {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>

          {/* Page Numbers */}
          {getPageNumbers().map((page, index) => (
            page === '...' ? (
              <span
                key={`ellipsis-${index}`}
                style={{
                  padding: '0 0.5rem',
                  color: 'var(--color-text-tertiary, #9ca3af)'
                }}
              >
                ...
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                disabled={disabled}
                style={page === currentPage ? activeButtonStyle : (disabled ? disabledButtonStyle : buttonStyle)}
                aria-label={t('pagination.goToPage', { page })}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            )
          ))}

          {/* Next Page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={disabled || currentPage === totalPages}
            style={disabled || currentPage === totalPages ? disabledButtonStyle : buttonStyle}
            title={t('common.nextPage')}
            aria-label={t('pagination.goToNextPage')}
          >
            {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>

          {/* Last Page */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={disabled || currentPage === totalPages}
            style={disabled || currentPage === totalPages ? disabledButtonStyle : buttonStyle}
            title={t('pagination.lastPage')}
            aria-label={t('pagination.goToLastPage')}
          >
            {isRTL ? <ChevronsLeft size={16} /> : <ChevronsRight size={16} />}
          </button>
        </div>
      )}
    </div>
  );
}
